import styled, { css } from 'styled-components'
import { Label } from '../styles/Typography'
import { theme } from '../../theme/theme'
import { ChartData, ChartStep } from './type'

type LegendData = (Omit<ChartData, 'value'> & { value?: number })[]

/** Legend entries for a set of steps. Pass `values` where a segment is too small to label. */
export const stepLegend = (steps: readonly ChartStep[], values?: Record<string, number>): LegendData =>
  steps.map(({ key, name, color }) => ({ name, color, value: values?.[key] }))

type LegendType = 'dots' | 'lines'
export type LegendVariant = 'default' | 'pill'
type LegendAlign = 'start' | 'end'
/** `sentence` keeps the label's own casing, for labels read as words rather than tags. */
type LegendCase = 'uppercase' | 'sentence'

type LegendProps = {
  data: LegendData
  type?: LegendType
  noLabelFlex?: boolean
  variant?: LegendVariant
  align?: LegendAlign
  textCase?: LegendCase
  /** For a legend stacked under the title. One sharing a ChartHeader row must stay at content
   * width or it crowds the title out. */
  fullWidth?: boolean
}

type MarkerProps = { $color?: string }

type MarkerShape = { width: number; height: number; radius: number }

const MARKER_SHAPE: Record<LegendType, MarkerShape> = {
  dots: { width: 10, height: 10, radius: 3 },
  lines: { width: 32, height: 4, radius: 2 },
}

export const ChartLegend = ({
  data,
  type = 'dots',
  noLabelFlex,
  variant = 'default',
  align = 'end',
  textCase = 'uppercase',
  fullWidth = false,
}: LegendProps) => {
  const sentenceCase = textCase === 'sentence'

  return (
    <LegendContainer $noLabelFlex={noLabelFlex} $type={type} $variant={variant} $align={align} $fullWidth={fullWidth}>
      {data.map(({ name, color, value }) => {
        const stringValue = value?.toString()
        return (
          <LegendFlex key={name} $variant={variant} $color={color}>
            <LegendMarker $color={color} $shape={MARKER_SHAPE[type]} />
            <LegendLabel $sentenceCase={sentenceCase}>{name}</LegendLabel>
            {stringValue && <LegendLabel $sentenceCase={sentenceCase}>({stringValue})</LegendLabel>}
          </LegendFlex>
        )
      })}
    </LegendContainer>
  )
}

export const StepLegend = ({ steps }: { steps: readonly ChartStep[] }) => (
  <ChartLegend align="start" noLabelFlex fullWidth textCase="sentence" data={stepLegend(steps)} />
)

const LegendLabel = styled(Label)<{ $sentenceCase: boolean }>`
  ${({ $sentenceCase }) =>
    $sentenceCase &&
    css`
      text-transform: none;
      letter-spacing: 0;
      font-weight: 400;
      font-size: ${theme.fontSize.small}px;
      line-height: 20px;
      color: ${theme.color.blueDark};
    `}
`

const LegendContainer = styled.div<{
  $type: LegendType
  $fullWidth?: boolean
  $noLabelFlex?: boolean
  $variant?: LegendVariant
  $align?: LegendAlign
}>`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({ $align }) => ($align === 'start' ? 'flex-start' : 'end')};
  gap: ${theme.spacing.mediumLarge}px;
  text-transform: capitalize;
  margin-bottom: ${theme.spacing.medium}px;

  ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}
  @media screen and (max-width: ${theme.breakpoint.xlarge}px) {
    flex-direction: ${({ $noLabelFlex }) => ($noLabelFlex ? 'row' : 'column')};
    align-items: flex-start;
    gap: ${theme.spacing.medium}px;
    margin-bottom: ${theme.spacing.small}px;

    ${({ $type }) =>
      $type === 'lines' &&
      `
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: ${theme.spacing.small}px ${theme.spacing.medium}px;
    `}
  }
`

const LegendMarker = styled.span<MarkerProps & { $shape: MarkerShape }>`
  flex: none;
  align-self: center;
  ${({ $shape }) => css`
    width: ${$shape.width}px;
    min-width: ${$shape.width}px;
    max-width: ${$shape.width}px;
    height: ${$shape.height}px;
    min-height: ${$shape.height}px;
    max-height: ${$shape.height}px;
    border-radius: ${$shape.radius}px;
  `}
  background-color: ${({ $color }) => $color || theme.color.black};
`

const LegendFlex = styled.div<MarkerProps & { $variant?: LegendVariant }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xsmall}px;
  ${({ $variant, $color }) =>
    $variant === 'pill' &&
    `
      padding: ${theme.spacing.tiny}px ${theme.spacing.small}px;
      border-radius: 999px;
      background-color: ${$color ?? theme.color.grey3}1a;
      border: 1px solid ${$color ?? theme.color.grey3}40;
    `}
`
